import React, {useCallback, useMemo} from 'react';
import {Modal} from "../../../../../components/modal/modal.jsx";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {AgGridReact} from "ag-grid-react";
import projectUtils from "../../../../../utils/project-utils.js";
import moment from "moment";
import {useQuery} from "@tanstack/react-query";
import citizenService from "../../../../../services/web/citizen-service.js";
import encounterService from "../../../../../services/web/encounter-service.js";
import bosService from "../../../../../services/web/bos-service.js";
import occurrenceService from "../../../../../services/web/occurrence-service.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {toast} from "react-toastify";
import citizenAggressorsListService from "../../../../../services/web/citizen-aggressors-list-service.js";

const endpoints = {
    total: (cpf) => citizenService.getCitizensByAggressor(cpf),
    bogcmTotal: (cpf) => bosService.getByCpf(cpf),
    encounterTotal: (cpf) => encounterService.encountersByCpf(cpf),
    occTotal: (cpf) => occurrenceService.findByCpf(cpf),
    visitTotal: (cpf) => citizenAggressorsListService.visitList(cpf),
};

const AggressorsListModal = ({aggressor, target, open, setOpen, boName}) => {
    const navigate = navigateHook();

    const theme = useMemo(() => aggridUtils.generalTheme(), [])

    const gridOptions = useMemo(() => ({localeText: aggridUtils.localization()}), [])

    const {data} = useQuery({
        queryFn: () => endpoints[target](aggressor),
        queryKey: ["aggressors-detail", aggressor, target],
        enabled: open && !!aggressor && !!target,
    });

    const columns = useMemo(() => {
        const columnMap = {
            total: [
                {field: "name", headerName: "Nome", flex: 2},
                {headerName: "CPF/CPNJ", valueGetter: ({data: {cpf, cnpj}}) => cpf ? projectUtils.formatCpfToView(cpf) : projectUtils.formatCnpjToView(cnpj)},
                {field: "protectionProgram", headerName: "Programa de proteção", flex: 1, valueFormatter: ({value}) => value?.map(i => i.name).join(", ")},
                {field: "registrationDt", headerName: "Data/hora do cadastro", flex: 1, valueFormatter: ({value}) => moment(value).format("DD/MM/YYYY HH:mm")},
            ],
            bogcmTotal: [
                {field: "id", headerName: "Número", flex: 1, comparator: aggridUtils.agGridNumberYearComparator},
                {field: "status", headerName: "Status", flex: 1},
                {field: "reason_name_view", headerName: "Natureza(s)", valueGetter: ({data}) => data.reason_name_view?.map(i => i).join(", "), flex: 2},
                {
                    field: "dtFact",
                    headerName: "D/H do fato",
                    flex: 1,
                    valueFormatter: ({data}) => {
                        if (!data.dtFact) return null;

                        if (data.dtFactUncertain === "true") {
                            return moment(data.dtFact).format("DD/MM/YYYY");
                        }

                        return  moment(data.dtFact).format("DD/MM/YYYY HH:mm");
                    },
                    comparator: aggridUtils.agGridSimpleDateComparator
                },
            ],
            encounterTotal: [
                {field: "namewar", headerName: "Abordado por", flex: 1},
                {field: "dt_start", headerName: "D/H inicial", flex: 1, valueFormatter: ({value}) => value ? moment.utc(value).local().format("DD/MM/YYYY HH:mm") : null, sort: "desc"},
                {field: "observation", headerName: "Observação", flex: 2},
            ],
            occTotal: [
                {field: "classification", headerName: "Classificação", flex: 1},
                {field: "reason", headerName: "Naturezas", flex: 2},
                {field: "dtIni", headerName: "D/H inicial do fato", valueGetter: ({data}) => data?.dtIni ? moment(data?.dtIni).format("DD/MM/YYYY HH:mm") : null, comparator: aggridUtils.agGridCompareDateTime},
                {field: "dtEnd", headerName: "D/H final do fato", valueGetter: ({data}) => data?.dtEnd ? moment(data?.dtEnd).format("DD/MM/YYYY HH:mm") : null, comparator: aggridUtils.agGridCompareDateTime},
            ],
            visitTotal: [
                { field: "dtAction", headerName: "Data/Hora", flex: 1, valueGetter: ({data}) => data?.dtAction ? moment.utc(data.dtAction).local().format("DD/MM/YYYY HH:mm") : null, comparator: aggridUtils.agGridCompareDateTime, sort: "desc" },
                { field: "citizenName", headerName: "Protegido", flex: 1, comparator: aggridUtils.nullLastComparator,},
                { field: "username", headerName: "Agente", flex: 1, comparator: aggridUtils.nullLastComparator },
                { field: "description", headerName: "Descrição", flex: 2, comparator: aggridUtils.nullLastComparator }
            ],
        }

        return columnMap[target]
    }, [target]);

    const title = useMemo(() => ({
        total: "SOS",
        bogcmTotal: boName,
        encounterTotal: "Abordagens",
        occTotal: "Ocorrências",
        visitTotal: "Visitas",
    }), [boName]);

    const onCellClicked = useCallback(({data}) => {
        const onClick = {
            total: () => navigate(`/sos/citizens/form/${data.id}`, true),
            bogcmTotal: () => {
                const id = data.id.replace('/','-');
                navigate(`/bo/redirect/${id}`, true)
            },
            encounterTotal: () => navigate(`/encounter/form/${data.id}`, true),
            visitTotal: () => navigate(`/sos/citizen/history/${data.citizenId}?history_id=${data.historyId}`, true),
            occTotal: () => {
                occurrenceService.openSentryViewOccurrence(data.id)
                    .then(() => {
                        toast.success("A ocorrência associada será aberta no SentryView.")
                    })
                    .catch(error => {
                        console.error(error);
                        toast.error("Ocorreu um erro ao abrir a ocorrência no SentryView.")
                    });
            }
        }

        onClick[target]?.();
    }, [navigate, target]);

    return (
        <Modal open={open} setOpen={setOpen} title={`Cadastros de ${title[target]} associados ao agressor`} overflow={true}>
            <Modal.Body>
                <div className={"h-[calc(100vh-245px)] aggrid-row-pointer"}>
                    <AgGridReact
                        rowHeight={32}
                        rowData={data ?? []}
                        columnDefs={columns ?? []}
                        pagination
                        paginationPageSize={100}
                        paginationPageSizeSelector={[20, 50, 100, 500]}
                        animateRows={false}
                        suppressCellFocus={true}
                        gridOptions={gridOptions}
                        onCellClicked={onCellClicked}
                        theme={theme}
                    />
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default AggressorsListModal;